/* ----- mixin for controlling the Integration flow ------

This mixin imports the appropriate integration mixin (from common/mixins/integrations folder) on the fly
(based on the header.enrichment property) and uses its actions for controlling user's flow (optin/re-optin/forgot pin/unsub etc).

For adding a new mixin, you should create a js file (in common/mixins/integrations folder) that extends the baseIntegration.js and
implement the appropriate actions there. A config entry for the new integration should also be added in the availableIntegrations
state object (located in store/modules/common/index.js).
*/

import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import Vue from 'vue';
import DigitalParametersMixin from '@/common/mixins/digital/digitalHelper';

const moment = require('moment/moment');

Vue.use(require('vue-moment'), {
  moment,
});

const IntegrationMixin = {
  mixins: [DigitalParametersMixin],
  data() {
    return {
      ERROR_REDIRECT_MESSAGE: '',
      SHOW_LOADING: false,
    };
  },
  methods: {
    ...mapActions('common', [
      'updateIntegrationInfo',
      'reOptinProcess',
      'storeOperator',
    ]),
    ...mapActions('profile', [
      'clearAllData',
    ]),
    ...mapMutations('profile', [
      'setRegistrationInfo',
    ]),
    ...mapMutations('common', [
      'setShowSelectOperatorModal',
      'setShowNoBalanceModal',
      'facebookReferral',
      'setCorrelationId',
      'updateProducedUniqueId',
    ]),
    /**
     * this method is used by components in order to init the integration flow (1st step)
     */
    registrationAction() {
      this.getPreRegistrationRedirectionAction()();
    },
    /**
     * this method calls the actions before redirecting the user to appropriate registration page (2nd Step)
     */
    getPreRegistrationRedirectionAction() {
      return this.getPreRegistrationRedirectionActionImpl();
    },
    /**
     * this method fetches the appropriate redirection url from backend and redirects user to that (3rd Step)
     */
    registrationRedirection() {
      const activeIntegration = this.getIntegrationConfig.config;
      let url = 'api/getRegisterUrl';
      const numOfParameters = activeIntegration.parameters.length;
      if (numOfParameters) url = `${url}?`;
      activeIntegration.parameters.forEach((element, index) => {
        url = `${url}${element.key}=${element.value}`;
        if (index + 1 !== numOfParameters) url = `${url}&`;
      });
      this.storeParametersToBrowserStorage();
      if (activeIntegration.storeDigitalParams) {
        url = `${url}&refUrl=${this.getRefUrl || ''}&clickid=${this.getClickId || ''}&publisherId=${this.getPublisherId || ''}&campaignId=${this.getCampaignId || ''}&friendRef=${this.fbReferral || ''}`;
      }
      this.$axios.get(url).then((response) => {
        const responseEnum = response.data.responseEnum;
        let redirectionUrl = response.data.data;
        if (responseEnum === 'OPERATION_SUCCESS') {
          this.toggleSelectOperatorModal(false);
          if (redirectionUrl && redirectionUrl.startsWith('http')) {
            if (activeIntegration.transferDigitalParams) {
              redirectionUrl = `${redirectionUrl}&refUrl=${this.getRefUrl || ''}&clickid=${this.getClickId || ''}&publisherId=${this.getPublisherId || ''}&campaignId=${this.getCampaignId || ''}`;
            }
            window.location.href = redirectionUrl;
          } else {
            sessionStorage.setItem('msisdn', redirectionUrl);
            this.$router.push('register2');
          }
        } else {
          this.ERROR_REDIRECT_MESSAGE = responseEnum;
        }
      });
    },
    /**
     * this method checks the url parameters for partner's response and do the appropriate actions to register user (4th Step - when partner redirects user back to us again)
     */
    getIntegrationParametersCheck() {
      return this.getIntegrationParametersCheckImpl();
    },

    // ----------- request/verify methods -------------------------------
    getAlternateRequestPinAction(isDailySubscription) {
      return this.getAlternateRequestPinActionImpl(isDailySubscription);
    },
    getAlternateVerifyPinAction(ti, ts) {
      return this.getAlternateVerifyPinActionImpl(ti, ts);
    },

    // ----------- reSub/unSub methods ----------------------------------
    /**
     * actions after successful unsub
     */
    getPostUnSubAction() {
      return this.getPostUnSubActionImpl();
    },
    alternateReSubAction() {
      return this.alternateReSubActionImpl();
    },
    standardReSubAction() {
      return this.reOptinProcess();
    },
    /**
     *  chooses appropriate resub action based on active integration
     */
    getAppropriateReSubAction() {
      const { msisdn, operatorName } = this.userDetails;
      const DuName = this.multiOperatorInfo.multiOperatorKeys.filter(item => item.includes('DU'))[0];
      const activeIntegration = this.getIntegrationConfig.config;
      if ((activeIntegration && activeIntegration.hasThirdPartyReSub) && operatorName === DuName) {
        this.alternateReSubAction()(msisdn, operatorName); // alternate resub with msisdn, operator
      } else if (this.getIntegrationConfig.integrationKey === this.availableIntegrations.VODAFONE_OMAN.name) {
        // Named branch, not the hasHeReSub flag: this operator's getFlowInfo() is null, so config is undefined.
        this.alternateReSubAction()(msisdn);
      } else if (activeIntegration && activeIntegration.hasHeReSub) {
        this.alternateReSubAction()(msisdn, operatorName); // alternate resub for HE, no need for msisdn, operator params
      } else {
        this.standardReSubAction().then((response) => {
          this.reSubCallback(response); // the component that want to use it, should implement it
        });
      }
    },

    // ------------- forgotPin methods --------------------------------------
    standardForgotProcess() {
      sessionStorage.setItem('altMode', 'false');
      this.$axios.get(`api/requestNewPin?msisdn=${this.user.msisdn}&lang=${this.getLocale}&operator=${this.user.operator}`).then((response) => {
        const res = response.data.responseEnum;
        if (res === 'OPERATION_SUCCESS') {
          this.setForgotPasswordInfo(response.data.data);
          this.getNextForgotPasswordStep()();
          this.user.msisdn = '';
          this.message = '';
        } else {
          if (res === 'MSISDN_INVALID') {
            this.message = 'visitor_forgot_password_invalid_msisdn_msg';
          } else if (res === 'ALREADY_REGISTERED') {
            this.message = 'visitor_forgot_password_msisdn_already_registered';
          } else if (res === 'MSISDN_NOT_EXIST') {
            this.message = 'visitor_forgot_password_msisdn_not_exists';
          } else if (res === 'MSISDN_BLACKLISTED') {
            this.message = 'visitor_forgot_password_msisdn_blacklisted';
          } else if (res === 'SERVER_ERROR') {
            this.message = 'home_profile_profile_edit_server_unvailable';
          } else if (res === 'BLOCKED_USER') {
            this.message = 'visitor_forgot_password_warning_msd_max_attempts';
          } else if (res === 'CAMPAIGN_CLOSED') {
            this.message = 'visitor_sign_up_campaign_closed';
          } else if ((res === 'THIRD_PARTY_PIN_REQUEST_FAILED') || (res === 'THIRD_PARTY_PIN_VERIFICATION_FAILED') || (res === 'THIRD_PARTY_SUBSCRIPTION_PIN_REQUEST_FAILED')) {
            this.message = 'visitor_sign_up_warning_third_party_error';
          }
          this.hasErrors = true;
        }
      });
    },
    alternateForgotProcess() {
      return this.alternateForgotProcessImpl();
    },
    standardForgotVerifyPinProcess() {
      this.$axios.get(`api/verifyPinForForgotPassword?msisdn=${this.forgotPasswordInfo}&pin=${this.pin}`).then((response) => {
        const resp = response.data.responseEnum;
        if (resp === 'OPERATION_SUCCESS') {
          this.setHashedPassForForgotPass(response.data.data.hashedPass);
          this.$router.push('forgotPassword3');
          this.message = '';
        } else {
          if (resp === 'PIN_EXPIRED') {
            this.message = 'visitor_sign_up_2_pin_expired';
          } else if (resp === 'WRONG_PIN') {
            this.message = 'visitor_forgot_password_2_warning_msg_wrong_verification_code';
          } else if (resp === 'BLOCKED_USER') {
            this.message = 'visitor_forgot_password_warning_msd_max_attempts';
          } else if (resp === 'SERVER_ERROR') {
            this.message = 'SERVER_ERROR';
          } else if ((resp === 'THIRD_PARTY_PIN_REQUEST_FAILED') || (resp === 'THIRD_PARTY_PIN_VERIFICATION_FAILED') || (resp === 'THIRD_PARTY_SUBSCRIPTION_PIN_REQUEST_FAILED')) {
            this.message = 'visitor_sign_up_warning_third_party_error';
          }
          this.hasErrors = true;
        }
      });
    },
    alternateForgotVerifyPinProcess() {
      return this.alternateForgotVerifyPinProcessImpl();
    },

    // ------------- integration helper methods -----------------------------------
    hasResub() {
      return this.getIntegrationConfig.config && this.getIntegrationConfig.config.resubOnForgot;
    },
    hasReSubOption() {
      return !(this.getIntegrationConfig.config && this.getIntegrationConfig.config.hasSubInHome);
    },
    disableUnsubOption() {
      return this.getIntegrationConfig.config && this.getIntegrationConfig.config.hideUnsubOptionInSettingsPage;
    },
    setActiveIntegrationInfo(info) {
      this.updateIntegrationInfo({ value: info });
    },
    toggleSelectOperatorModal(value) {
      this.setShowSelectOperatorModal({ value });
    },
    getSelectOperatorModalChoice() {
      return this.showSelectOperatorModal;
    },
    getNoBalanceModal() {
      return this.showNoBalanceModal;
    },
    toggleNoBalanceModal(value) {
      this.setShowNoBalanceModal({ value });
    },
    hasSubUnSubOption() {
      return this.getIntegrationConfig.config && this.getIntegrationConfig.config.hasSubInHome;
    },
    hasServerTrack() {
      return this.getIntegrationConfig.config && this.getIntegrationConfig.config.hasServerTrack;
    },
    clearLoginInfo() {
      let currentLocale = localStorage.getItem('locale');
      if (currentLocale === undefined || currentLocale === null) {
        currentLocale = this.getLocale;
      }
      const soundsOn = localStorage.getItem('soundsOn');
      localStorage.clear();
      localStorage.setItem('locale', currentLocale);
      localStorage.setItem('soundsOn', soundsOn);
      sessionStorage.clear();
      if (this.userDetails.availableGames === 0) {
        // in order to know the last log in time
        sessionStorage.setItem('zeroBalancePopUp', moment().format('L'));
      }
      this.clearAllData();
    },
    proceedToAutoLogin(to) {
      if (to.query.user_auth_token && to.query.username) {
        this.storeLoginInfo({
          token: to.query.user_auth_token,
          rememberMeParameter: true,
          username: to.query.username,
        });
        this.getUserLoginDetails().then(() => {
          this.updateUserLanguage();
          this.$router.push('home');
        });
      } else this.showPage(true);
    },
    checkIfIntegrationRequires2StepOnly() {
      return this.checkIfIsOnly2Step();
    },
    getIntegrationByName(name = this.getIntegrationConfig.integrationKey) {
      if (!name) {
        return '';
      }
      const integrationKey = Object.keys(this.availableIntegrations).find(key => this.availableIntegrations[key].name === name);
      return { key: integrationKey, value: this.availableIntegrations[integrationKey] };
    },
  },
  computed: {
    ...mapState('common', [
      'fbReferral',
      'banner',
      'availableIntegrations',
      'multiOperatorInfo',
      'showSelectOperatorModal',
      'showNoBalanceModal',
      'userDetails',
      'producedUniqueId',
    ]),
    ...mapState('profile', [
      'registrationInfoMsisdn',
    ]),
    ...mapGetters('common', [
      'getClickId',
      'getRefUrl',
      'getCampaignId',
      'getPublisherId',
      'getIntegrationConfig',
    ]),
    ...mapGetters('i18n', [
      'getLocale',
    ]),
  },
};

export default IntegrationMixin;



// WEBPACK FOOTER //
// ./src/common/mixins/integrations/integrationControll.js